import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, Plus, Edit2, Trash2, Eye, Ship, FileText, Loader2 } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const SeaMtDocumentList = () => {
  const { docketId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [docketNo, setDocketNo] = useState('');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const getAuth = useCallback(() => ({
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    'Content-Type': 'application/json',
  }), []);

  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);
      const [docsRes, docketRes] = await Promise.all([
        fetch(`${backendUrl}/api/sea-mt-documents?docket_id=${docketId}`, { headers: getAuth() }),
        fetch(`${backendUrl}/api/dockets/${docketId}`, { headers: getAuth() }),
      ]);
      if (docsRes.ok) setDocs(await docsRes.json());
      if (docketRes.ok) {
        const d = await docketRes.json();
        setDocketNo(d.system_docket_no || docketId);
      }
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to load BL / MT Documents', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [docketId, backendUrl, getAuth, toast]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this BL / MT Document?')) return;
    try {
      setDeletingId(docId);
      const res = await fetch(`${backendUrl}/api/sea-mt-documents/${docId}`, {
        method: 'DELETE',
        headers: getAuth(),
      });
      if (!res.ok) throw new Error('Delete failed');
      toast({ title: 'Deleted', description: 'BL / MT Document removed' });
      setDocs(prev => prev.filter(d => d.id !== docId));
    } catch (err) {
      toast({ title: 'Error', description: err.message, variant: 'destructive' });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(`/dockets/edit/${docketId}`)}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back to Docket
            </Button>
            <div>
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                <Ship className="w-5 h-5 text-teal-600" />
                BL / MT Documents
              </h1>
              <p className="text-xs text-gray-500">Docket: {docketNo}</p>
            </div>
          </div>
          <Button
            onClick={() => navigate(`/dockets/${docketId}/sea-mt-documents/new`)}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-mt-doc-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add BL / MT Document
          </Button>
        </div>

        {/* Table */}
        <Card className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {docs.length === 0 ? (
            <div className="p-12 text-center">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500 font-medium">No BL / MT Documents yet</p>
              <p className="text-xs text-gray-400 mt-1">Click "Add BL / MT Document" to create the first BL / MT Document for this docket.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="mt-doc-table">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-10">Sr.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Job No.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Consignor</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Consignee</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Notify Party</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">MTD / BL Number</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Shipment Ref.</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Port of Loading</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Port of Discharge</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Vessel &amp; Voyage</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Place of Delivery</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {docs.map((doc, idx) => (
                    <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                      <td className="px-4 py-3 font-medium text-gray-800">{doc.job_no || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate">{doc.consignor_shipper || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate">{doc.consignee || '—'}</td>
                      <td className="px-4 py-3 text-gray-700 max-w-[160px] truncate">{doc.notify_party || '—'}</td>
                      <td className="px-4 py-3 font-mono text-teal-700">{doc.mtd_bl_number || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{doc.shipment_reference_no || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{doc.port_of_loading || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">{doc.port_of_discharge || '—'}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {[doc.vessel, doc.voyage_no].filter(Boolean).join(' / ') || '—'}
                      </td>
                      <td className="px-4 py-3 text-gray-600">{doc.place_of_delivery || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            data-testid={`view-mt-doc-${idx}`}
                            title="View / Print"
                            onClick={() => navigate(`/dockets/${docketId}/sea-mt-documents/${doc.id}/print`)}
                            className="p-1.5 rounded bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`edit-mt-doc-${idx}`}
                            title="Edit"
                            onClick={() => navigate(`/dockets/${docketId}/sea-mt-documents/${doc.id}/edit`)}
                            className="p-1.5 rounded bg-amber-50 text-amber-600 hover:bg-amber-100 transition-colors"
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            data-testid={`delete-mt-doc-${idx}`}
                            title="Delete"
                            onClick={() => handleDelete(doc.id)}
                            disabled={deletingId === doc.id}
                            className="p-1.5 rounded bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-50"
                          >
                            {deletingId === doc.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

      </div>
    </div>
  );
};

export default SeaMtDocumentList;
